import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import StatCard from '../components/ui/StatCard';
import SectionCard from '../components/ui/SectionCard';
import DataTable from '../components/ui/DataTable';
import dashboardAPI from '../../../services/dashboardService';
import { Users, UserPlus, UserCheck, TrendingUp } from 'lucide-react';

// Maps backend usersByType keys to existing translation keys
const USER_TYPE_LABEL_KEYS = {
    PET_GUARDIAN: 'adminDashboard.userTypes.petGuardian',
    VETERINARIAN: 'adminDashboard.userTypes.veterinarian',
    SERVICE_PROVIDER: 'adminDashboard.userTypes.serviceProvider',
    FIRM: 'adminDashboard.userTypes.firm',
    ADMIN: 'adminDashboard.userTypes.admin',
    CONTENT_ADMIN: 'adminDashboard.userTypes.contentAdmin',
    DEV_ADMIN: 'adminDashboard.userTypes.devAdmin',
    CLINIC_MODERATOR: 'adminDashboard.userTypes.clinicModerator'
};

const DetailedUsersPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const RECENT_USER_COLUMNS = [
        { key: 'name', header: t('systemDashboard.users.columns.name') },
        { key: 'type', header: t('systemDashboard.users.columns.type') },
        {
            key: 'created_at',
            header: t('systemDashboard.users.columns.registered'),
            width: '140px',
            render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : t('systemDashboard.common.na'))
        }
    ];

    const filters = externalFilters || localFilters;

    const fetchStats = useCallback(async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getUserStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch user stats', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    useEffect(() => {
        fetchStats(filters);
    }, [fetchStats, filters]);

    const userTypeData = useMemo(() => {
        if (!stats?.usersByType) return [];
        return Object.entries(stats.usersByType).map(([key, value]) => ({
            name: USER_TYPE_LABEL_KEYS[key]
                ? t(USER_TYPE_LABEL_KEYS[key])
                : key.replace(/_/g, ' ').toLowerCase(),
            value
        }));
    }, [stats?.usersByType, t]);

    const graphData = stats?.graph_data || [];

    if (loading || !stats) {
        return <div className="sd-loading-message">{t('systemDashboard.detailed.loading')}</div>;
    }

    return (
        <DetailedPageLayout
            title={t('systemDashboard.detailed.titles.users')}
            tab="users"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard label={t('systemDashboard.users.totalUsers')} value={stats.totalUsers} icon={Users} color="bg-blue-500" />
                <StatCard label={t('systemDashboard.users.newUsers')} value={stats.newUsers} icon={UserPlus} color="bg-emerald-500" />
                <StatCard label={t('systemDashboard.users.activeUsers')} value={stats.activeUsers} icon={UserCheck} color="bg-violet-500" />
            </div>

            <div className="sd-grid sd-grid--charts">
                <SectionCard title={t('systemDashboard.users.userDistribution')}>
                    <CustomDonutChart
                        data={userTypeData}
                        totalValue={stats.totalUsers}
                        title=""
                    />
                </SectionCard>
                <SectionCard title={t('systemDashboard.users.userGrowth')} className="sd-card--wide">
                    <CustomAreaChart data={graphData} color="#3b82f6" title="" />
                </SectionCard>
            </div>

            <div className="sd-grid sd-grid--charts sd-grid--last">
                <SectionCard title={t('systemDashboard.users.registrationDynamics')} className="sd-card--wide">
                    <CustomBarChart data={graphData} title="" />
                </SectionCard>
                <SectionCard title={t('systemDashboard.users.recentRegistrations')} icon={TrendingUp}>
                    <DataTable
                        columns={RECENT_USER_COLUMNS}
                        data={stats.recentUsers || []}
                        emptyText={t('systemDashboard.users.noRecentRegistrations')}
                    />
                </SectionCard>
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedUsersPage;
